import React from 'react';
import './Pagination.css';

/**
 * Pagination
 * ==========
 * Reusable DynamoDB-style numbered pagination for iCOP lists/tables.
 *
 * Behaviour (matches the AWS DynamoDB console):
 *  - Shows a fixed page size (default 10) per page.
 *  - Page numbers are shown on top; clicking a number JUMPS to that page and
 *    REPLACES the visible rows (it never appends to the bottom).
 *  - Prev / Next arrows, with the current page highlighted.
 *  - Long ranges are collapsed with an ellipsis (1 … 4 5 [6] 7 8 … 20).
 *
 * This is a controlled component: the parent owns `page` state and slices its
 * own data. Use the `usePagination` hook (usePagination.js) to avoid repeating
 * the slice math in every list.
 *
 * Props:
 *  - page         (number)  current 1-based page
 *  - pageCount    (number)  total number of pages
 *  - onPageChange (fn)      called with the new 1-based page number
 *  - totalItems   (number, optional) for the "Showing X–Y of N" label
 *  - pageSize     (number, optional) rows per page, for the label
 *  - siblingCount (number, optional) page numbers to show each side of current
 */
const DOTS = '…';

const range = (start, end) => {
  const out = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
};

// Build the list of page tokens to render: numbers + DOTS placeholders.
const buildPages = (page, pageCount, siblingCount) => {
  // How many page buttons we show at most: first + last + current + siblings + 2 dots.
  const totalNumbers = siblingCount * 2 + 5;
  if (pageCount <= totalNumbers) {
    return range(1, pageCount);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, pageCount);

  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < pageCount - 1;

  if (!showLeftDots && showRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    return [...range(1, leftItemCount), DOTS, pageCount];
  }
  if (showLeftDots && !showRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    return [1, DOTS, ...range(pageCount - rightItemCount + 1, pageCount)];
  }
  return [1, DOTS, ...range(leftSibling, rightSibling), DOTS, pageCount];
};

const Pagination = ({
  page,
  pageCount,
  onPageChange,
  totalItems = null,
  pageSize = 10,
  siblingCount = 1,
}) => {
  if (!pageCount || pageCount <= 1) {
    // Nothing to paginate; still render an optional count label for context.
    if (totalItems != null && totalItems > 0) {
      return (
        <div className="icop-pagination single">
          <span className="icop-pagination-info">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
        </div>
      );
    }
    return null;
  }

  const current = Math.min(Math.max(page, 1), pageCount);
  const pages = buildPages(current, pageCount, siblingCount);

  const go = (p) => {
    if (p < 1 || p > pageCount || p === current) return;
    onPageChange(p);
  };

  const start = (current - 1) * pageSize + 1;
  const end = totalItems != null ? Math.min(current * pageSize, totalItems) : current * pageSize;

  return (
    <div className="icop-pagination">
      {totalItems != null && (
        <span className="icop-pagination-info">
          Showing {start}–{end} of {totalItems}
        </span>
      )}
      <nav className="icop-pagination-pages" aria-label="Pagination">
        <button
          type="button"
          className="icop-page-btn nav"
          onClick={() => go(current - 1)}
          disabled={current <= 1}
          aria-label="Previous page"
        >
          ‹
        </button>

        {pages.map((p, i) =>
          p === DOTS ? (
            <span key={`dots-${i}`} className="icop-page-dots">{DOTS}</span>
          ) : (
            <button
              key={p}
              type="button"
              className={`icop-page-btn ${p === current ? 'active' : ''}`}
              onClick={() => go(p)}
              aria-current={p === current ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          className="icop-page-btn nav"
          onClick={() => go(current + 1)}
          disabled={current >= pageCount}
          aria-label="Next page"
        >
          ›
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
